import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../contexts/ProfileContext';
import { useStreamAuth } from '../hooks/useStreamAuth';

const ProfileDropdown = () => {
  const navigate = useNavigate();
  const { logout, user } = useStreamAuth();
  const { profiles, currentProfile, setActiveProfile, clearCurrentProfile } = useProfile();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Helper function to get profile avatar with Google profile for main account only
  const getProfileAvatar = profile => {
    // Only use Google profile picture for the main account holder (Owner profile)
    // For all  profiles, use ChillFree profile avatar if we have it
    if (profile?.profile_thumb) {
      return profile.profile_thumb;
    }
    if (profile?.profile_name === 'Owner' && user?.picture) {
      return user.picture;
    }
    return null;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle profile switching
  const handleProfileSwitch = async profile => {
    const success = await setActiveProfile(profile);
    if (success) {
      setIsOpen(false);
      // No page reload needed - profile context will update automatically
    }
  };

  // Handle manage profiles
  const handleManageProfiles = () => {
    setIsOpen(false);
    navigate('/profiles');
  };

  // Handle switch profiles (clear current and go to selection)
  const handleSwitchProfiles = () => {
    clearCurrentProfile();
    setIsOpen(false);
    // Navigate to profiles page without any query parameters
    navigate('/profiles', { replace: true });
  };

  // Handle dashboard navigation
  const handleDashboard = () => {
    setIsOpen(false);
    navigate('/dashboard');
  };

  // Handle logout
  const handleLogout = () => {
    setIsOpen(false);
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  // Don't render if no current profile
  if (!currentProfile) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 transition-colors rounded-lg hover:bg-white/10"
        aria-label="Profile menu"
      >
        {/* Profile Avatar */}
        <div className="flex-shrink-0 w-8 h-8 overflow-hidden bg-gray-600 rounded-full">
          {getProfileAvatar(currentProfile) ? (
            <img
              src={getProfileAvatar(currentProfile)}
              alt={currentProfile.profile_name}
              className="object-cover w-full h-full"
            />
          ) : (
            <div className="flex items-center justify-center w-full h-full text-sm font-bold text-white">
              {currentProfile.profile_name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        {/* Profile Name */}
        <span className="hidden text-sm font-medium text-white sm:block">
          {currentProfile.profile_name}
        </span>

        {/* Dropdown Arrow */}
        <svg
          className={`w-4 h-4 text-white transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 z-50 w-64 mt-2 border border-gray-700 rounded-lg shadow-xl top-full bg-secondary">
          {/* Current Profile Header */}
          <div className="px-4 py-3 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 overflow-hidden bg-gray-600 rounded-full">
                {getProfileAvatar(currentProfile) ? (
                  <img
                    src={getProfileAvatar(currentProfile)}
                    alt={currentProfile.profile_name}
                    className="object-cover w-full h-full"
                  />
                ) : (
                  <div className="flex items-center justify-center w-full h-full text-lg font-bold text-white">
                    {currentProfile.profile_name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div>
                <div className="font-medium text-white">{currentProfile.profile_name}</div>
                <div className="text-sm text-white/60">Current Profile</div>
              </div>
            </div>
          </div>

          {/* Other Profiles */}
          {profiles.length > 1 && (
            <div className="py-2">
              <div className="px-4 py-2 text-xs font-medium tracking-wide uppercase text-white/60">
                Switch to
              </div>
              {profiles
                .filter(profile => profile._id !== currentProfile._id)
                .map(profile => (
                  <button
                    key={profile._id}
                    onClick={() => handleProfileSwitch(profile)}
                    className="flex items-center w-full gap-3 px-4 py-2 text-left transition-colors hover:bg-white/10"
                  >
                    <div className="flex-shrink-0 w-8 h-8 overflow-hidden bg-gray-600 rounded-full">
                      {getProfileAvatar(profile) ? (
                        <img
                          src={getProfileAvatar(profile)}
                          alt={profile.profile_name}
                          className="object-cover w-full h-full"
                        />
                      ) : (
                        <div className="flex items-center justify-center w-full h-full text-sm font-bold text-white">
                          {profile.profile_name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <span className="text-sm text-white">{profile.profile_name}</span>
                  </button>
                ))}
            </div>
          )}

          {/* Actions */}
          <div className="py-2 border-t border-gray-700">
            <button
              onClick={handleDashboard}
              className="flex items-center w-full gap-3 px-4 py-2 text-left transition-colors hover:bg-white/10"
            >
              <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 bg-gray-600 rounded-full">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <span className="text-sm text-white">Dashboard</span>
            </button>

            <button
              onClick={handleManageProfiles}
              className="flex items-center w-full gap-3 px-4 py-2 text-left transition-colors hover:bg-white/10"
            >
              <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 bg-gray-600 rounded-full">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
                  />
                </svg>
              </div>
              <span className="text-sm text-white">Manage Profiles</span>
            </button>

            <button
              onClick={handleSwitchProfiles}
              className="flex items-center w-full gap-3 px-4 py-2 text-left transition-colors hover:bg-white/10"
            >
              <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 bg-gray-600 rounded-full">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                  />
                </svg>
              </div>
              <span className="text-sm text-white">Switch Profiles</span>
            </button>

            <div className="pt-2 mt-2 border-t border-gray-700">
              <button
                onClick={handleLogout}
                className="flex items-center w-full gap-3 px-4 py-2 text-left transition-colors hover:bg-white/10"
              >
                <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 bg-gray-600 rounded-full">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                </div>
                <span className="text-sm text-white">Log Out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
